import type { KickLogoColor } from "@design-system/KickLogo/KickLogo";
import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomySize,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import {
    kickLogoFormats,
    kickLogoPaints,
    kickLogoSizePixels,
    kickLogoSizes,
    type KickLogoFormat,
} from "./KickLogoPlayground.lib";

// Mined from designSystem/KickLogo/KickLogo.module.scss; geometry from common/constants/kickLogoPaths.ts.

/** One Figma variant: artwork, colour and transparency. */
export interface KickLogoAnatomyCombination {
    format: KickLogoFormat;
    color: KickLogoColor;
    transparent: boolean;
}

/** The anatomy variant id for a playground combination, e.g. `icon-circle-white-transparent`. */
export const kickLogoAnatomyVariantId = ({
    format,
    color,
    transparent,
}: KickLogoAnatomyCombination) =>
    `${format}-${color}${transparent ? "-transparent" : ""}`;

const combinations: readonly KickLogoAnatomyCombination[] =
    kickLogoFormats.flatMap((format) =>
        kickLogoPaints.map((paint) => ({ format, ...paint })),
    );

/** Reverses `kickLogoAnatomyVariantId`, so the schematic draws the same logo. */
export const kickLogoAnatomyCombination = (variantId: string) =>
    combinations.find(
        (combination) => kickLogoAnatomyVariantId(combination) === variantId,
    );

const parts: readonly AnatomyPart[] = [
    {
        id: "tile",
        label: "Tile",
        element:
            "path.tile: the 48 x 48 rounded square or circle (with the mark subpaths when transparent)",
    },
    {
        id: "mark",
        label: "Mark",
        element: "path.mark: two bars and a slash on the tile",
    },
    {
        id: "wordmark",
        label: "Wordmark",
        element: 'path.wordmark: "kick" beside the tile, full variant only',
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
];

const tileColorTokens: Record<KickLogoColor, `--${string}`> = {
    black: "--fg-primary",
    white: "--fg-on-solid",
};

const markColorTokens: Record<KickLogoColor, `--${string}`> = {
    black: "--fg-on-solid",
    white: "--fg-primary",
};

const sharedBindings: readonly AnatomyBinding[] = [
    {
        partId: "tile",
        property: "fill",
        token: tileColorTokens.black,
        via: "--kick-logo-tile",
        note: "Figma Color/Foreground/primary. Declared on .kickLogo, so black is the default.",
    },
    {
        partId: "mark",
        property: "fill",
        token: markColorTokens.black,
        via: "--kick-logo-mark",
        note: "Figma Color/Foreground/on-solid.",
    },
];

const whiteBindings: readonly AnatomyBinding[] = [
    {
        partId: "tile",
        property: "fill",
        token: tileColorTokens.white,
        via: "--kick-logo-tile",
        note: '[data-color="white"] swaps the tile and mark colours.',
    },
    {
        partId: "mark",
        property: "fill",
        token: markColorTokens.white,
        via: "--kick-logo-mark",
    },
];

const transparentBindings: readonly AnatomyBinding[] = [
    {
        partId: "tile",
        property: "fill-rule",
        literal: "evenodd",
        note: "One path.tile.cutout holds the tile and the mark subpaths, so no seam shows at the cut.",
    },
    {
        partId: "mark",
        property: "fill",
        literal: "none (cut out)",
        note: "The mark is a hole in the tile; the surface shows through.",
    },
];

const squareTileBinding: AnatomyBinding = {
    partId: "tile",
    property: "corner",
    literal: "size / 8",
    note: "Vector geometry (r 6 in the 48 viewBox): 6px at 48, 4px at 32, 2px at 16.",
};

const squareMarkBinding: AnatomyBinding = {
    partId: "mark",
    property: "box",
    literal: "9.68..37.55 x 12.9..35.09 of 48",
};

const formatBindings: Record<KickLogoFormat, readonly AnatomyBinding[]> = {
    full: [
        squareTileBinding,
        squareMarkBinding,
        {
            partId: "wordmark",
            property: "root width",
            literal: "calc(var(--kick-logo-size) * 150 / 48)",
            via: "--kick-logo-size",
            note: '[data-variant="full"] widens the root to the 150 x 48 viewBox.',
        },
        {
            partId: "wordmark",
            property: "box",
            literal: "69.74..149.14 x 7.33..39.48 of 150 x 48",
        },
    ],
    "icon-square": [squareTileBinding, squareMarkBinding],
    "icon-circle": [
        {
            partId: "tile",
            property: "corner",
            literal: "circle",
        },
        {
            partId: "mark",
            property: "box",
            literal: "11.47..35.85 x 14.29..33.71 of 48",
            note: "The square mark at 87.5%, re-centred; not the square mark in a circle.",
        },
    ],
};

const formatSelectors: Record<KickLogoFormat, string> = {
    full: '[data-variant="full"]',
    "icon-square": '[data-variant="icon"][data-shape="square"]',
    "icon-circle": '[data-variant="icon"][data-shape="circle"]',
};

const wordmarkFill = (color: KickLogoColor): readonly AnatomyBinding[] => [
    {
        partId: "wordmark",
        property: "fill",
        token: tileColorTokens[color],
        via: "--kick-logo-tile",
        note: "The wordmark always shares the tile colour.",
    },
];

const toVariant = (combination: KickLogoAnatomyCombination): AnatomyVariant => {
    const { format, color, transparent } = combination;
    const colorSelector = `[data-color="${color}"]`;
    return {
        id: kickLogoAnatomyVariantId(combination),
        label: [format, color, transparent ? "transparent" : "opaque"].join(
            " · ",
        ),
        selector: `${formatSelectors[format]}${colorSelector}${transparent ? "[data-transparent]" : ""}`,
        restingBindings: [
            ...formatBindings[format],
            ...(color === "white" ? whiteBindings : []),
            ...(transparent ? transparentBindings : []),
            ...(format === "full" ? wordmarkFill(color) : []),
        ],
        stateBindings: {},
    };
};

const sizes: readonly AnatomySize[] = kickLogoSizes.map((size) => ({
    id: size,
    label: `${kickLogoSizePixels[size]}px`,
    selector: `[data-size="${size}"]`,
    bindings: [
        {
            partId: "tile",
            property: "width / height",
            literal: `${kickLogoSizePixels[size]}px`,
            via: "--kick-logo-size",
            note: "The tile spans the root's full height; the whole vector scales with it.",
        },
    ],
}));

export const kickLogoAnatomy: ComponentAnatomySpec = {
    label: "KickLogo",
    schematic: "kickLogo",
    rootSelector: ".kickLogo",
    variantAxisLabel: "Figma variant",
    parts,
    states,
    sharedBindings,
    variants: combinations.map(toVariant),
    sizes,
};
